import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AuthRepository } from "../repositories/AuthRepository";
import { useDispatch } from "react-redux";
import { LOCALE } from "../../properties/Locale";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";
import { UserRepository } from "../repositories/UserRepository";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import { CapWidget } from "@pitininja/cap-react-widget";
import { SETTINGS } from "../../Settings";

export default function Registration() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState();
  const [widgetKey, setWidgetKey] = useState(0);

  const [data, setData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (name, value) => {
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRegister = () => {
    setLoading(true);
    setError();
    data.captchaToken = captchaToken;
    UserRepository.registerUser(data)
      .then((response) => {
        setLoading(false);
        console.log("response", response);
        if (response && response.data) {
          navigate("/login");
          dispatch(
            notifyShowSuccessMessage(
              "Успешна регистрација. Проверете ја вашата меил пошта за потврда за регистрацијата."
            )
          );
        }
      })
      .catch((error) => {
        resetCaptcha();
        setError(ErrorMessageResolver.resolve(error));
        setLoading(false);
      });
  };

  const resetCaptcha = () => {
    setWidgetKey((prev) => prev + 1); // Forces a full remount → resets
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleRegister();
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box className="registration-container">
      <Container
        className="registration-container-inner"
        sx={{ marginTop: { md: "-150px", sm: "-150px" } }}
      >
        <Grid item xs={12} className="registration-logo-container">
          <img
            id="rm-coat-of-arms"
            src={`${process.env.PUBLIC_URL}/assets/mt-logo.png`}
            alt="RM"
            width="75"
          />
          <Typography variant={"body2"} sx={{ color: "white", mb: 1 }}>
            Министерство за транспорт
          </Typography>
          <Typography variant={"h6"} sx={{ color: "white" }}>
            Систем за електронско аплицирање за трајна и временска <br />
            измена на режим за сообраќај
          </Typography>
        </Grid>

        <Grid item xs={12} className="registration-inner-grid">
          <Typography className="registration-heading">
            <b>Регистрација</b>
          </Typography>
          {error && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error" variant="filled" sx={{ mb: 0 }}>
                <Typography>{LOCALE.getString(error) || error}</Typography>
              </Alert>
            </Box>
          )}
          <Grid
            container
            spacing={0.5}
            className="registration-inputs-container"
          >
            <Grid item xs={12}>
              <TextField
                size="small"
                name="new-username"
                fullWidth
                label="Корисничко име"
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
                onChange={(e) => handleChange("username", e.target.value)}
                value={data?.username || ""}
                className="registration-textfield"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                name="email"
                label="Меил адреса"
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
                onChange={(e) => handleChange("email", e.target.value)}
                value={data?.email || ""}
                className="registration-textfield"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                name="firstName"
                fullWidth
                label="Име"
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
                onChange={(e) => handleChange("firstName", e.target.value)}
                value={data?.firstName || ""}
                className="registration-textfield"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                name="lastName"
                label="Презиме"
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
                onChange={(e) => handleChange("lastName", e.target.value)}
                value={data?.lastName || ""}
                className="registration-textfield"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                name="phone-number"
                label="Телефон"
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
                onChange={(e) => handleChange("phone", e.target.value)}
                value={data?.phone || ""}
                className="registration-textfield"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                label="Лозинка"
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
                onChange={(e) => handleChange("password", e.target.value)}
                value={data?.password || ""}
                className="registration-textfield"
                onKeyDown={(e) => handleKeyDown(e)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleShowPassword();
                        }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            toggleShowPassword();
                          }
                        }}
                      >
                        {showPassword ? (
                          <FaRegEye className="registration-show-hide-password-icon" />
                        ) : (
                          <FaRegEyeSlash className="registration-show-hide-password-icon" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                name="confirm-password"
                fullWidth
                type={showPassword ? "text" : "password"}
                label="Потврди лозинка"
                autoComplete="off"
                inputProps={{ spellCheck: "false" }}
                onChange={(e) =>
                  handleChange("confirmPassword", e.target.value)
                }
                value={data?.confirmPassword || ""}
                className="registration-textfield"
                onKeyDown={(e) => handleKeyDown(e)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleShowPassword();
                        }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            toggleShowPassword();
                          }
                        }}
                      >
                        {showPassword ? (
                          <FaRegEye className="registration-show-hide-password-icon" />
                        ) : (
                          <FaRegEyeSlash className="registration-show-hide-password-icon" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Grid container sx={{ mt: "10px", mb: "10px" }} spacing={1}>
            <Grid item xs={12} md={4} sx={{ mt: 1 }}></Grid>
            <Grid item xs={12} md={4} sx={{ mt: 1, mb: 2 }}>
              {(captchaToken || !captchaToken) && (
                <CapWidget
                  key={widgetKey}
                  endpoint={SETTINGS.CAPTCHA_API_URL}
                  onSolve={(token) => {
                    console.log(`Challenge succeeded, token : ${token}`);
                    setCaptchaToken(token);
                  }}
                  onError={(err) => {
                    console.log(`Challenge failed`);
                    setCaptchaToken(null);
                  }}
                  customWaspUrl={`${window.location.origin}/cap/cap_wasm.js`}
                  className="captcha1"
                  i18nInitial="Верификувај ме"
                  i18nVerifying="Се верификува..."
                  i18nSolved="Верификуван"
                  i18nError="Настана грешка"
                  i18nVerifyAriaLabel="Кликни за да се верификуваш"
                  i18nVerifyingAriaLabel="Се верификува"
                  i18nVerifiedAriaLabel="Успешно верификуван"
                  i18nErrorAriaLabel="Грешка при верификација"
                />
              )}
            </Grid>
            <Grid item xs={12} md={4} sx={{ mt: 1 }}></Grid>
            <Grid item xs={12} md={5}>
              <Button
                variant="outlined"
                color="error"
                size="large"
                fullWidth
                onClick={() => navigate("/login")}
              >
                Назад кон најава
              </Button>
            </Grid>
            <Grid item xs={12} md={2}></Grid>
            <Grid item xs={12} md={5}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={loading || !captchaToken}
                onClick={() => handleRegister()}
              >
                {!loading ? (
                  "Регистрирај се"
                ) : (
                  <CircularProgress size={20} id="circular-progress" />
                )}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
